import React from "react";
import { useTranslation } from "react-i18next";
import TextField from "@components/forms/TextField/TextField";
import Switch from "@components/forms/Switch/Switch";
import Color from "@components/forms/Color/Color";
import Label from "@components/forms/Label/Label";
import { EditorPrimitive, clamp } from "./primitives";
import { CANVAS_VARIABLES } from "./variables";

type PropertiesPanelProps = {
    primitive: EditorPrimitive | null,
    displayDuration: number,
    onChange: (p: EditorPrimitive) => void,
    onChangeDuration: (seconds: number) => void,
};

const toInt = (value: string, fallback = 0): number => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? fallback : n;
};

function NumberField(props: { label: string, value: number, min?: number, max?: number, onChange: (n: number) => void }) {
    return (
        <TextField
            type="number"
            label={props.label}
            value={props.value}
            min={props.min}
            max={props.max}
            onChange={(v) => {
                let n = toInt(v, props.value);
                if (props.min !== undefined) n = Math.max(props.min, n);
                if (props.max !== undefined) n = Math.min(props.max, n);
                props.onChange(n);
            }}
        />
    );
}

function PointFields(props: { label: string, x: number, y: number, onChange: (x: number, y: number) => void }) {
    return (
        <div className="col-12">
            <Label>{props.label}</Label>
            <div className="row g-2">
                <div className="col-6"><NumberField label="X" value={props.x} onChange={(n) => props.onChange(n, props.y)} /></div>
                <div className="col-6"><NumberField label="Y" value={props.y} onChange={(n) => props.onChange(props.x, n)} /></div>
            </div>
        </div>
    );
}

function PropertiesPanel(props: PropertiesPanelProps) {
    const { t } = useTranslation();
    const p = props.primitive;

    if (!p) {
        return (
            <div className="canvas-properties">
                <div className="canvas-properties-empty">{t('page.scenes.canvas.no_selection')}</div>
                <div className="row g-3 mt-1">
                    <div className="col-12">
                        <NumberField
                            label={t('page.scenes.data.display_duration')}
                            value={props.displayDuration}
                            min={1}
                            max={600}
                            onChange={props.onChangeDuration}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const patch = (changes: Partial<EditorPrimitive>) => props.onChange({ ...p, ...changes } as EditorPrimitive);

    return (
        <div className="canvas-properties">
            <div className="canvas-properties-title">{t(`page.scenes.canvas.tool.${p.type}`)}</div>
            <div className="row g-3">
                <div className="col-12">
                    <Color label={t('page.scenes.canvas.field.color')} value={p.color} onChange={(v) => patch({ color: v })} />
                </div>

                {p.type === 'text' && (
                    <>
                        <div className="col-12">
                            <Label>{t('page.scenes.canvas.field.text')}</Label>
                            <textarea
                                className="canvas-text-input"
                                value={p.text}
                                maxLength={100}
                                onChange={(e) => patch({ text: e.target.value })}
                            />
                        </div>
                        <div className="col-12">
                            <Label>{t('page.scenes.canvas.field.insert_variable')}</Label>
                            <select
                                className="canvas-variable-select"
                                value=""
                                onChange={(e) => { if (e.target.value) patch({ text: (p.text + e.target.value).slice(0, 100) }); }}
                            >
                                <option value="">{t('page.scenes.canvas.field.insert_variable_hint')}</option>
                                {CANVAS_VARIABLES.map((v) => (
                                    <option key={v.token} value={v.token}>
                                        {t(v.labelKey)}{v.sample ? ` (${v.sample})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-6">
                            <NumberField label={t('page.scenes.canvas.field.size')} value={p.size} min={1} max={7} onChange={(n) => patch({ size: clamp(n, 1, 7) })} />
                        </div>
                        <div className="col-6 d-flex align-items-end">
                            <Switch label={t('page.scenes.canvas.field.wrap')} checked={p.wrap} onChange={(c) => patch({ wrap: c })} />
                        </div>
                        <PointFields label={t('page.scenes.canvas.field.position')} x={p.position.x} y={p.position.y} onChange={(x, y) => patch({ position: { x, y } })} />
                    </>
                )}

                {p.type === 'rectangle' && (
                    <>
                        <PointFields label={t('page.scenes.canvas.field.position')} x={p.position.x} y={p.position.y} onChange={(x, y) => patch({ position: { x, y } })} />
                        <div className="col-6"><NumberField label={t('page.scenes.canvas.field.width')} value={p.width} min={0} onChange={(n) => patch({ width: n })} /></div>
                        <div className="col-6"><NumberField label={t('page.scenes.canvas.field.height')} value={p.height} min={0} onChange={(n) => patch({ height: n })} /></div>
                        <div className="col-12"><NumberField label={t('page.scenes.canvas.field.radius')} value={p.radius} min={0} onChange={(n) => patch({ radius: n })} /></div>
                    </>
                )}

                {p.type === 'circle' && (
                    <>
                        <PointFields label={t('page.scenes.canvas.field.center')} x={p.position.x} y={p.position.y} onChange={(x, y) => patch({ position: { x, y } })} />
                        <div className="col-12"><NumberField label={t('page.scenes.canvas.field.radius')} value={p.radius} min={0} onChange={(n) => patch({ radius: n })} /></div>
                    </>
                )}

                {p.type === 'arc' && (
                    <>
                        <PointFields label={t('page.scenes.canvas.field.center')} x={p.position.x} y={p.position.y} onChange={(x, y) => patch({ position: { x, y } })} />
                        <div className="col-6"><NumberField label={t('page.scenes.canvas.field.radius')} value={p.radius} min={0} onChange={(n) => patch({ radius: n })} /></div>
                        <div className="col-6"><NumberField label={t('page.scenes.canvas.field.thickness')} value={p.thickness} min={1} onChange={(n) => patch({ thickness: Math.max(1, n) })} /></div>
                        <div className="col-6"><NumberField label={t('page.scenes.canvas.field.start_angle')} value={p.start_angle} min={0} max={360} onChange={(n) => patch({ start_angle: clamp(n, 0, 360) })} /></div>
                        <div className="col-6"><NumberField label={t('page.scenes.canvas.field.end_angle')} value={p.end_angle} min={0} max={360} onChange={(n) => patch({ end_angle: clamp(n, 0, 360) })} /></div>
                    </>
                )}

                {p.type === 'pixel' && (
                    <PointFields label={t('page.scenes.canvas.field.position')} x={p.position.x} y={p.position.y} onChange={(x, y) => patch({ position: { x, y } })} />
                )}

                {p.type === 'line' && (
                    <>
                        <PointFields label={t('page.scenes.canvas.field.point_a')} x={p.pointA.x} y={p.pointA.y} onChange={(x, y) => patch({ pointA: { x, y } })} />
                        <PointFields label={t('page.scenes.canvas.field.point_b')} x={p.pointB.x} y={p.pointB.y} onChange={(x, y) => patch({ pointB: { x, y } })} />
                        <div className="col-12"><NumberField label={t('page.scenes.canvas.field.thickness')} value={p.thickness} min={1} onChange={(n) => patch({ thickness: Math.max(1, n) })} /></div>
                    </>
                )}

                {p.type === 'triangle' && (
                    <>
                        <PointFields label={t('page.scenes.canvas.field.point_a')} x={p.pointA.x} y={p.pointA.y} onChange={(x, y) => patch({ pointA: { x, y } })} />
                        <PointFields label={t('page.scenes.canvas.field.point_b')} x={p.pointB.x} y={p.pointB.y} onChange={(x, y) => patch({ pointB: { x, y } })} />
                        <PointFields label={t('page.scenes.canvas.field.point_c')} x={p.pointC.x} y={p.pointC.y} onChange={(x, y) => patch({ pointC: { x, y } })} />
                    </>
                )}
            </div>
        </div>
    );
}

export default PropertiesPanel;
